/**
 * `pwa_installed` event (plan Lot C.2, point 3).
 *
 * Android/desktop Chrome fires `appinstalled`; iOS Safari never does (no
 * such event exists there), so the app is added to the home screen without
 * any signal at the moment it happens. The fallback: the very first launch
 * in standalone mode (`isStandalonePwa()`) is itself good evidence the icon
 * was just added — checked against a `chouette.pwa.installed` localStorage
 * flag so it only ever fires once per device. The `appinstalled` handler
 * sets the same flag, so a device that gets both signals (Android/desktop)
 * is still only counted once.
 */
import { postEvent } from "./dataSource";
import { isStandalonePwa } from "./platform";
import { isNative } from "../native/platform";
import { detectInstallPlatform } from "./installNudge";

const INSTALLED_FLAG_KEY = "chouette.pwa.installed";

function hasSeenInstall(): boolean {
  try {
    return localStorage.getItem(INSTALLED_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

function markInstallSeen(): void {
  try {
    localStorage.setItem(INSTALLED_FLAG_KEY, "1");
  } catch {
    // Private browsing — worst case an iOS device reports pwa_installed
    // again on its next standalone launch, since the flag never sticks.
  }
}

function platformMeta(): { platform: string } {
  return { platform: detectInstallPlatform(navigator.userAgent, navigator.maxTouchPoints) };
}

/** Call once at startup (see main.tsx). */
export function initInstallEventTracking(): void {
  if (typeof window === "undefined") return;
  // A store app is not a PWA install: counting it would mix the two in
  // the admin stat.
  if (isNative()) return;
  window.addEventListener("appinstalled", () => {
    markInstallSeen();
    void postEvent("pwa_installed", platformMeta());
  });
  if (isStandalonePwa() && !hasSeenInstall()) {
    markInstallSeen();
    void postEvent("pwa_installed", platformMeta());
  }
}
